<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import CarsAnimation from '@/components/CarsAnimation.vue';
import CityMap from '@/components/CityMap.vue';
import FilterMenu from '@/components/FilterMenu.vue';
import LanguageSelectorDialog from '@/components/LanguageSelectorDialog.vue';
import LhoistLogo from '@/components/LhoistLogo.vue';
import PoiDetailsCard from '@/components/PoiDetailsCard.vue';
import PoiDetailsLayout from '@/components/PoiDetailsLayout.vue';
import PoiDetailsSubpois from '@/components/PoiDetailsSubpois.vue';
import PoiPin from '@/components/PoiPin.vue';
import { Button, Icon } from '@/components/ui';
import WebGLZoneAnimations from '@/components/webgl/WebGLZoneAnimations.vue';
import ZoneAnimations from '@/components/ZoneAnimations.vue';
import { useGlobal, useImageDownloader, useMap, usePois, useFlag } from '@/composables';
import type { HttpsLink, LanguageCode, Poi } from '@/types';

const {
  pois,
  isPoiAmongFiltered,
  selectedPoi,
  selectPoi,
  isPoiHighQuality,
  isPoiDisplayed,
  filteredPois,
} = usePois();
const { t } = useI18n();
const { language } = useGlobal();

const { mapTransitionState, zoomIn, zoomOut, zoomTo, focusOnArea, apply } = useMap();
const { isPoiFocused, isLoadingScreenTransitionstartActive } = useGlobal();
const { handleLoadAsset, isFinished } = useImageDownloader();

const { getFlag } = useFlag();

const urls: { [key: string]: URL } = {};

const getPoi50 = (name: string) => {
  const key = `poi50-${name}`;
  if (!(key in urls)) {
    urls[key] = new URL(`/src/assets/images/Maps/elements_50/${name}.avif?url`, import.meta.url);
  }

  return urls[key].href;
};
const getPoi100 = (name: string) => {
  const key = `poi100-${name}`;
  if (!(key in urls)) {
    urls[key] = new URL(`/src/assets/images/Maps/elements_100/${name}.avif?url`, import.meta.url);
  }

  return urls[key].href;
};

const handlePoiPinClick = (poi: Poi) => {
  apply(
    () => {
      selectPoi(poi.name);
      isPoiFocused.value = true;
      focusOnArea(poi);
    },
    { saveState: true },
  );
};

const handlePoiDetailsCardClose = () => {
  apply(
    () => {
      selectedPoi.value = null;
      isPoiFocused.value = false;
    },
    {
      restoreState: true,
      withTransition: false,
    },
  );
};
</script>

<template>
  <CityMap>
    <div v-show="isPoiHighQuality" data-poi-hq class="absolute size-full">
      <img
        v-memo="[filteredPois]"
        v-for="poi in pois"
        :key="poi.name"
        v-show="isPoiAmongFiltered(poi.name)"
        class="pointer-events-none absolute z-rgb-pois select-none"
        :style="{
          transform: `translate3d(${poi.left}px, ${poi.top}px, 0)`,
          width: `${poi.width}px`,
          height: `${poi.height}px`,
        }"
        :src="getPoi100(poi.name)"
        :alt="poi.alt"
        @load="!isFinished ? handleLoadAsset() : null"
      />
    </div>
    <div v-show="!isPoiHighQuality" data-poi-lq class="absolute size-full">
      <img
        v-memo="[filteredPois]"
        v-for="poi in pois"
        :key="poi.name"
        v-show="isPoiAmongFiltered(poi.name)"
        class="custom-relative pointer-events-none absolute z-rgb-pois select-none"
        :style="{
          transform: `translate3d(${poi.left}px, ${poi.top}px, 0)`,
          width: `${poi.width}px`,
          height: `${poi.height}px`,
        }"
        :src="getPoi50(poi.name)"
        :alt="poi.alt"
        @load="!isFinished ? handleLoadAsset() : null"
      />
    </div>
    <div data-poi-animated-pois-webgl class="absolute size-full">
      <WebGLZoneAnimations v-once />
    </div>
    <div data-poi-animated-pois-sprite class="absolute size-full">
      <ZoneAnimations v-once />
    </div>
    <div data-poi-cars class="absolute size-full">
      <CarsAnimation v-once />
    </div>
    <div data-poi-pins class="absolute size-full">
      <PoiPin
        v-memo="[mapTransitionState[2], filteredPois]"
        @click.stop="handlePoiPinClick(poi)"
        tabindex="-1"
        class="absolute z-poi-pins origin-top-left select-none"
        v-for="poi in pois"
        :key="poi.name"
        v-show="isPoiDisplayed(poi.name)"
        :variant="poi.category"
        :style="{
          left: `${poi.pin.left}px`,
          top: `${poi.pin.top}px`,
          transform: `scale(${0.65 / mapTransitionState[2]}) translate(-50%, -100%)`,
        }"
      />
    </div>
    <template #viewport>
      <div class="pointer-events-none absolute left-0 top-0 z-ui h-dvh w-dvw p-2 lg:p-10">
        <div class="relative size-full">
          <div data-viewport-logo class="pointer-events-auto absolute left-0 top-0">
            <LhoistLogo
              class="h-[65px] w-[130px] rounded-br-[40px]"
              variant="color"
              background="matte-glass"
            />
          </div>
          <div
            data-viewport-i18n
            class="pointer-events-auto absolute right-0 top-0"
            :class="!isLoadingScreenTransitionstartActive && '!visible z-10'"
          >
            <LanguageSelectorDialog
              v-if="!isLoadingScreenTransitionstartActive"
              triggerClass="bg-white text-primary"
            />
            <Button v-else as="a" target="_blank" :href="t(`contact-cta.locale.${language}.url`)">
              <span>{{ t('contact-cta.label') }}</span>
              <div class="h-[1em] bg-current" />
            </Button>
          </div>
          <div data-viewport-poi-details class="absolute bottom-0 left-0">
            <PoiDetailsLayout class="h-[calc(100dvh_-_6.5rem)] lg:h-[calc(100dvh_-_10.75rem)]">
              <template #pins>
                <PoiDetailsSubpois v-if="selectedPoi" />
              </template>
              <template #card>
                <PoiDetailsCard
                  class="pointer-events-auto w-[calc(100vw_-_1rem)] lg:max-w-[592px]"
                  :data="selectedPoi"
                  @close="handlePoiDetailsCardClose"
                  v-if="selectedPoi"
                />
              </template>
            </PoiDetailsLayout>
          </div>
          <div
            data-viewport-filters
            class="pointer-events-auto absolute bottom-0 right-0"
            :class="isPoiFocused && 'hidden lg:block'"
          >
            <FilterMenu />
          </div>
          <div
            v-if="!isPoiFocused"
            data-viewport-zoom-controls
            class="pointer-events-auto absolute bottom-16 left-0 wide:hidden"
          >
            <div class="flex flex-col gap-1 lg:gap-2">
              <Button variant="icon" @click="() => apply(() => zoomIn(2))">
                <Icon class="size-4" name="plus" />
              </Button>
              <Button variant="icon" @click="() => apply(() => zoomOut(2))">
                <Icon class="size-4" name="minus" />
              </Button>
              <Button variant="icon" @click="() => apply(() => zoomTo(9999))">
                <Icon class="size-4" name="expand" />
              </Button>
              <LanguageSelectorDialog triggerClass="bg-white text-primary" v-slot="{ open }">
                <Button variant="icon" @click="open">
                  <img class="size-6 rounded-full" :src="getFlag(language)" alt="" />
                </Button>
              </LanguageSelectorDialog>
            </div>
          </div>
        </div>
      </div>
    </template>
  </CityMap>
</template>
